import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import gibletLogo from '@/assets/giblet-logo.png';
import { CheckCircle2 } from 'lucide-react';

export default function DemoCallForKnowledgeReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [curatorEmail, setCuratorEmail] = useState('karen.watson@curt.org');
  const [curatorMessage, setCuratorMessage] = useState('');

  const bountyAmount = state.bountyAmount || 0;
  const creatorPct = state.creatorPct || 50;
  const contributorPct = state.contributorPct || 45;
  const confirmerPct = state.confirmerPct || 5;

  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <div className="max-w-2xl w-full space-y-8 text-center">
          <div className="flex justify-center mb-8">
            <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
          </div>
          <CheckCircle2 className="mx-auto h-16 w-16 text-green-500" />
          <h1 className="text-3xl font-bold text-foreground">CfK Submitted Successfully!</h1>
          <p className="text-muted-foreground">
            Your Call for Knowledge has been submitted. The Creator and Contributors will be notified.
          </p>
          <Button onClick={() => navigate('/demo-call-for-knowledge/rewards', { state })}>
            View Knowledge Rewards Distribution
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">Review Call for Knowledge</h1>
          <p className="text-lg text-muted-foreground">Confirm all details before submitting</p>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Functional Area:</span>
              <div className="font-medium text-foreground">{state.functionalAreaName || 'N/A'}</div>
            </div>
            <div>
              <span className="text-muted-foreground">CfS Connection:</span>
              <div className="font-medium text-foreground">
                {state.connectedToCfS ? state.selectedCfSTitle : 'Standalone CfK'}
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="font-semibold text-foreground mb-2">Knowledge Base</h3>
            <div className="text-sm font-medium text-foreground">{state.kbTitle}</div>
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{state.kbScope}</p>
          </div>

          {state.hasBounty && (
            <div className="border-t border-border pt-4">
              <h3 className="font-semibold text-foreground mb-2">Bounty Distribution</h3>
              <div className="bg-primary/10 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Total Available:</span>
                  <span className="font-bold">${bountyAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Creator ({creatorPct}%):</span>
                  <span>${(bountyAmount * creatorPct / 100).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Contributor ({contributorPct}%):</span>
                  <span>${(bountyAmount * contributorPct / 100).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Confirmer / Giblet ({confirmerPct}%):</span>
                  <span>${(bountyAmount * confirmerPct / 100).toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-border pt-4">
            <h3 className="font-semibold text-foreground mb-2">Acceptance Criteria</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{state.kbAcceptanceCriteria}</p>
          </div>

          <div className="border-t border-border pt-4 space-y-3">
            <h3 className="font-semibold text-foreground">Contact Creator</h3>
            <div className="space-y-2">
              <Label>Creator Email</Label>
              <Input value={curatorEmail} onChange={(e) => setCuratorEmail(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Textarea
                value={curatorMessage}
                onChange={(e) => setCuratorMessage(e.target.value)}
                placeholder="Add a message for the Creator..."
                rows={3}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate('/demo-call-for-knowledge/details')} className="min-w-[120px]">
            Back
          </Button>
          <Button onClick={() => setShowConfirmation(true)} className="min-w-[120px]">
            Submit CfK
          </Button>
        </div>
      </div>
    </div>
  );
}
